/**
 * Handles a `removed-from-cart` event: resolves the removed item's index and
 * announces its removal via aria-live.
 *
 * @param event - The `removed-from-cart` CustomEvent.
 * @param products - The component's current product list.
 * @param isTunnel - Whether the current flow is a tunnel purchase flow.
 * @param setPendingFocusIndex - Setter for the pending focus index.
 * @param setCartRemoveMessage - Setter for the aria-live message.
 * @param updateComplete - The component's `updateComplete` promise.
 */
export function handleRemovedFromCart(
  event: Event,
  products: ReadonlyArray<{ isbn?: string; isbn13?: string; title?: string }>,
  isTunnel: boolean,
  setPendingFocusIndex: (index: number | null) => void,
  setCartRemoveMessage: (msg: string) => void,
  updateComplete: Promise<boolean>
): void {
  const detail = (event as CustomEvent).detail;
  const removedIndex = products.findIndex(
    (p) =>
      (detail?.isbn !== undefined && p.isbn === detail.isbn) ||
      (detail?.isbn13 !== undefined && p.isbn13 === detail.isbn13)
  );
  const product = removedIndex >= 0 ? products[removedIndex] : undefined;
  const title = (isTunnel ? detail?.title : product?.title) || 'Item';

  setPendingFocusIndex(removedIndex >= 0 ? removedIndex : null);

  // Reset then re-set across two renders so aria-live re-announces on every removal.
  setCartRemoveMessage('');
  updateComplete.then(() => setCartRemoveMessage(`${title} has been removed.`));
}

/**
 * Moves focus to the product card at `removedIndex` after a cart item is removed.
 *
 * @param renderRoot - The render root of the cart component.
 * @param removedIndex - The zero-based list index of the item that was removed.
 * @param remainingCount - The number of product cards still in the list after removal.
 * @param onEmpty - Called when no product cards remain.
 */
export function focusCartCardAfterRemoval(
  renderRoot: HTMLElement | DocumentFragment,
  removedIndex: number,
  remainingCount: number,
  onEmpty: () => void
): void {
  const cards = Array.from(renderRoot.querySelectorAll<HTMLElement>('mhecomm-product-card-display'));
  if (cards.length === 0) {
    onEmpty();
    return;
  }

  const targetCard = cards[Math.min(removedIndex, remainingCount - 1)];
  const firstFocusable = targetCard.shadowRoot?.querySelector<HTMLElement>(
    'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"])'
  );

  if (firstFocusable) {
    firstFocusable.focus();
  } else {
    targetCard.setAttribute('tabindex', '-1');
    targetCard.focus();
  }
}
